import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import type { HttpTargetSystem } from '../types';
import {
    listTargetSystems,
    getTargetSystem,
    createTargetSystem,
    updateTargetSystem,
    deleteTargetSystem,
} from '../api/http-route-api';

/** 新建目标系统时的空白模板 */
function blankSystem(): HttpTargetSystem {
    return {
        id: 0,
        system_code: '',
        system_name: '',
        system_type: '',
        base_url: '',
        auth_type: 'none',
        auth_config_json: '{}',
        connect_timeout_ms: 5000,
        read_timeout_ms: 30000,
        enabled: 1,
        remark: '',
        created_at: '',
        updated_at: '',
    };
}

@customElement('http-target-system-view')
export class HttpTargetSystemView extends LitElement {
    static styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
        sans-serif;
    }

    .page-wrap {
      display: grid;
      grid-template-columns: 16rem minmax(0, 1fr);
      gap: 1.5rem;
      align-items: start;
    }

    @media (max-width: 900px) {
      .page-wrap {
        grid-template-columns: 1fr;
      }
    }

    .sidebar {
      padding: 1rem 0.75rem;
    }

    .sidebar-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-bottom: 0.65rem;
      padding: 0 0.25rem;
    }

    .sidebar-title {
      margin: 0;
      font-size: 0.8125rem;
      font-weight: 600;
      color: #486581;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      flex: 1 1 auto;
      min-width: 0;
    }

    .btn-new {
      flex: 0 0 auto;
      border: none;
      background: #2684ff;
      color: white;
      font-size: 0.75rem;
      font-weight: 600;
      padding: 0.4rem 0.65rem;
      border-radius: 6px;
      cursor: pointer;
      white-space: nowrap;
      transition: background 0.15s;
    }

    .btn-new:hover {
      background: #005cc5;
    }

    .sidebar-list {
      list-style: none;
      margin: 0;
      padding: 0;
      max-height: min(70vh, 520px);
      overflow-y: auto;
    }

    .sidebar-item {
      margin: 0;
      padding: 0;
    }

    .sidebar-link {
      display: block;
      width: 100%;
      text-align: left;
      border: none;
      background: transparent;
      cursor: pointer;
      border-radius: 8px;
      padding: 0.65rem 0.75rem;
      font-size: 0.8125rem;
      color: #334e68;
      transition: background 0.15s, color 0.15s;
      border: 1px solid transparent;
    }

    .sidebar-link:hover {
      background: #f0f4f8;
      color: #102a43;
    }

    .sidebar-link.active {
      background: #e3f2ff;
      color: #0f172a;
      border-color: #90cdf4;
      font-weight: 600;
    }

    .sidebar-code {
      display: block;
      font-weight: 600;
      color: inherit;
    }

    .sidebar-name {
      display: block;
      font-size: 0.75rem;
      color: #627d98;
      margin-top: 0.2rem;
    }

    .sidebar-link.active .sidebar-name {
      color: #486581;
    }

    .sidebar-empty {
      font-size: 0.8125rem;
      color: #829ab1;
      padding: 0.5rem 0.75rem;
    }

    .panel {
      background: white;
      border-radius: 8px;
      padding: 1.25rem 1.5rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
      border: 1px solid #e1e4e8;
    }

    .panel h2 {
      margin: 0 0 1rem;
      font-size: 1rem;
      font-weight: 600;
      color: #102a43;
      padding-bottom: 0.75rem;
      border-bottom: 1px solid #e1e4e8;
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem 1.5rem;
    }

    @media (max-width: 720px) {
      .form-grid {
        grid-template-columns: 1fr;
      }
    }

    .form-grid .span-2 {
      grid-column: 1 / -1;
    }

    .field {
      margin-bottom: 0.5rem;
    }

    label {
      display: block;
      margin-bottom: 0.35rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: #334e68;
    }

    input[type='text'],
    input[type='number'],
    select,
    textarea {
      width: 100%;
      box-sizing: border-box;
      padding: 0.5rem 0.65rem;
      border-radius: 6px;
      border: 1px solid #bcccdc;
      font-size: 0.875rem;
      outline: none;
      transition: border-color 0.2s;
      font-family: inherit;
    }

    input:focus,
    select:focus,
    textarea:focus {
      border-color: #2684ff;
    }

    textarea {
      min-height: 80px;
      resize: vertical;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.8125rem;
      line-height: 1.45;
    }

    textarea.auth-config-area {
      min-height: 100px;
    }

    .actions {
      margin-top: 1rem;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
    }

    .btn-primary {
      border: none;
      padding: 0.55rem 1.1rem;
      border-radius: 6px;
      font-weight: 500;
      font-size: 0.875rem;
      cursor: pointer;
      background-color: #2684ff;
      color: white;
      transition: background-color 0.2s;
    }

    .btn-primary:hover:not(:disabled) {
      background-color: #005cc5;
    }

    .btn-primary:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    .btn-outline {
      border: 1px solid #bcccdc;
      background: white;
      color: #334e68;
      padding: 0.55rem 1.1rem;
      border-radius: 6px;
      font-weight: 500;
      font-size: 0.875rem;
      cursor: pointer;
      transition: border-color 0.2s, color 0.2s;
    }

    .btn-outline:hover:not(:disabled) {
      border-color: #2684ff;
      color: #2684ff;
    }

    .btn-outline:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    .btn-danger {
      border: none;
      padding: 0.55rem 1.1rem;
      border-radius: 6px;
      font-weight: 500;
      font-size: 0.875rem;
      cursor: pointer;
      background: #dc2626;
      color: white;
      transition: background 0.2s;
    }

    .btn-danger:hover:not(:disabled) {
      background: #b91c1c;
    }

    .btn-danger:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    .field-hint {
      font-size: 0.75rem;
      color: #627d98;
      margin-top: 0.35rem;
      line-height: 1.45;
    }

    .persist-hint {
      margin-top: 0.5rem;
      font-size: 0.8125rem;
      color: #047857;
      white-space: pre-line;
    }

    .persist-hint.warn {
      color: #b45309;
    }

    .persist-hint.err {
      color: #b91c1c;
    }

    .enabled-badge {
      display: inline-block;
      font-size: 0.6875rem;
      font-weight: 600;
      padding: 0.15rem 0.5rem;
      border-radius: 10px;
      margin-left: 0.4rem;
    }

    .enabled-badge.on {
      background: #d1fae5;
      color: #065f46;
    }

    .enabled-badge.off {
      background: #fee2e2;
      color: #991b1b;
    }
  `;

    @state() private systems: HttpTargetSystem[] = [];
    @state() private selectedCode: string | null = null;
    @state() private form: HttpTargetSystem = blankSystem();
    @state() private saving = false;
    @state() private loading = false;
    @state() private persistHint = '';
    @state() private persistHintKind: 'info' | 'warn' | 'err' = 'info';

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        void this.fetchList();
    }

    private setPersistHint(msg: string, kind: 'info' | 'warn' | 'err' = 'info') {
        this.persistHint = msg;
        this.persistHintKind = kind;
    }

    private async fetchList() {
        try {
            const res = await listTargetSystems();
            if (Array.isArray(res)) {
                this.systems = res;
            }
        } catch (err) {
            console.error('获取目标系统列表失败:', err);
            this.setPersistHint('获取列表失败', 'err');
        }
    }

    private async loadSystem(code: string) {
        this.loading = true;
        this.setPersistHint('');
        try {
            const data = await getTargetSystem(code);
            this.form = { ...data };
            this.selectedCode = code;
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            this.setPersistHint(`加载失败: ${msg}`, 'err');
        } finally {
            this.loading = false;
        }
    }

    private createNew() {
        this.selectedCode = null;
        this.form = blankSystem();
        this.setPersistHint('新建目标系统：填写信息后保存', 'info');
    }

    private async saveSystem() {
        const code = this.form.system_code.trim();
        if (!code) {
            this.setPersistHint('系统编码不能为空', 'warn');
            return;
        }
        if (!this.form.system_name.trim()) {
            this.setPersistHint('系统名称不能为空', 'warn');
            return;
        }

        this.saving = true;
        this.setPersistHint('');

        try {
            if (this.selectedCode) {
                // 更新
                const { id, created_at, updated_at, ...updateData } = this.form;
                await updateTargetSystem(this.selectedCode, updateData);
                this.setPersistHint('更新成功', 'info');
            } else {
                // 创建
                const { id, created_at, updated_at, ...createData } = this.form;
                await createTargetSystem(createData as HttpTargetSystem);
                this.selectedCode = code;
                this.setPersistHint('创建成功', 'info');
            }
            await this.fetchList();
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            this.setPersistHint(`保存失败: ${msg}`, 'err');
        } finally {
            this.saving = false;
        }
    }

    private async deleteSystem() {
        const code = this.selectedCode;
        if (!code) {
            this.setPersistHint('没有选中要删除的目标系统', 'warn');
            return;
        }
        if (!confirm(`确定要删除目标系统 [${code}] 吗？此操作不可恢复！`)) {
            return;
        }
        try {
            await deleteTargetSystem(code);
            this.setPersistHint(`已删除 ${code}`, 'info');
            this.createNew();
            await this.fetchList();
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            this.setPersistHint(`删除失败: ${msg}`, 'err');
        }
    }

    private updateFormField(field: keyof HttpTargetSystem, value: string | number) {
        this.form = { ...this.form, [field]: value };
    }

    private renderSidebar() {
        return html`
      <aside class="panel sidebar" aria-label="目标系统列表">
        <div class="sidebar-head">
          <h2 class="sidebar-title">目标系统列表</h2>
          <button type="button" class="btn-new" @click=${() => this.createNew()}>+ 新建</button>
        </div>
        ${this.systems.length === 0
                ? html`<p class="sidebar-empty">暂无已配置的目标系统</p>`
                : html`
            <ul class="sidebar-list">
              ${this.systems.map(
                    (sys) => html`
                  <li class="sidebar-item">
                    <button
                      type="button"
                      class="sidebar-link ${this.selectedCode === sys.system_code ? 'active' : ''}"
                      @click=${() => this.loadSystem(sys.system_code)}
                    >
                      <span class="sidebar-code"
                        >${sys.system_code}
                        <span class="enabled-badge ${sys.enabled ? 'on' : 'off'}"
                          >${sys.enabled ? '启用' : '停用'}</span
                        >
                      </span>
                      <span class="sidebar-name">${sys.system_name || '—'}</span>
                    </button>
                  </li>
                `
                )}
            </ul>
          `}
      </aside>
    `;
    }

    private renderForm() {
        const hintClass =
            this.persistHintKind === 'err' ? 'err' : this.persistHintKind === 'warn' ? 'warn' : '';

        return html`
      <section class="panel" aria-labelledby="form-title">
        <h2 id="form-title">
          ${this.selectedCode
                ? `编辑目标系统: ${this.selectedCode}`
                : '新建目标系统'}
        </h2>

        <div class="form-grid">
          <div class="field">
            <label for="system_code">系统编码 *</label>
            <input
              id="system_code"
              type="text"
              .value=${this.form.system_code}
              ?disabled=${!!this.selectedCode || this.loading}
              placeholder="如 health-platform"
              @input=${(e: Event) =>
                    this.updateFormField('system_code', (e.target as HTMLInputElement).value)}
            />
            <div class="field-hint">唯一标识，创建后不可修改</div>
          </div>

          <div class="field">
            <label for="system_name">系统名称 *</label>
            <input
              id="system_name"
              type="text"
              .value=${this.form.system_name}
              ?disabled=${this.loading}
              placeholder="如 健康监测平台"
              @input=${(e: Event) =>
                    this.updateFormField('system_name', (e.target as HTMLInputElement).value)}
            />
          </div>

          <div class="field">
            <label for="system_type">系统类型</label>
            <input
              id="system_type"
              type="text"
              .value=${this.form.system_type}
              ?disabled=${this.loading}
              placeholder="如 REST, gRPC, WebSocket"
              @input=${(e: Event) =>
                    this.updateFormField('system_type', (e.target as HTMLInputElement).value)}
            />
          </div>

          <div class="field">
            <label for="base_url">基础 URL *</label>
            <input
              id="base_url"
              type="text"
              .value=${this.form.base_url}
              ?disabled=${this.loading}
              placeholder="https://robot.zpgotsafe.com"
              @input=${(e: Event) =>
                    this.updateFormField('base_url', (e.target as HTMLInputElement).value)}
            />
            <div class="field-hint">管控平台填 HTTPS 域名即可，无需 :5173（443 反代）</div>
          </div>

          <div class="field">
            <label for="auth_type">鉴权方式</label>
            <select
              id="auth_type"
              .value=${this.form.auth_type}
              ?disabled=${this.loading}
              @change=${(e: Event) =>
                    this.updateFormField('auth_type', (e.target as HTMLSelectElement).value)}
            >
              <option value="none">无鉴权</option>
              <option value="token">Token</option>
              <option value="basic">Basic Auth</option>
              <option value="bearer">Bearer Token</option>
              <option value="api_key">API Key</option>
            </select>
          </div>

          <div class="field">
            <label for="enabled">启用状态</label>
            <select
              id="enabled"
              .value=${String(this.form.enabled)}
              ?disabled=${this.loading}
              @change=${(e: Event) =>
                    this.updateFormField(
                        'enabled',
                        Number((e.target as HTMLSelectElement).value)
                    )}
            >
              <option value="1">启用</option>
              <option value="0">停用</option>
            </select>
          </div>

          <div class="field">
            <label for="connect_timeout_ms">连接超时 (ms)</label>
            <input
              id="connect_timeout_ms"
              type="number"
              .value=${String(this.form.connect_timeout_ms)}
              ?disabled=${this.loading}
              min="100"
              max="60000"
              @input=${(e: Event) =>
                    this.updateFormField(
                        'connect_timeout_ms',
                        Number((e.target as HTMLInputElement).value)
                    )}
            />
            <div class="field-hint">默认 5000ms</div>
          </div>

          <div class="field">
            <label for="read_timeout_ms">读取超时 (ms)</label>
            <input
              id="read_timeout_ms"
              type="number"
              .value=${String(this.form.read_timeout_ms)}
              ?disabled=${this.loading}
              min="100"
              max="600000"
              @input=${(e: Event) =>
                    this.updateFormField(
                        'read_timeout_ms',
                        Number((e.target as HTMLInputElement).value)
                    )}
            />
            <div class="field-hint">默认 30000ms；仅当路由规则未设超时时作为兜底</div>
          </div>

          <div class="field span-2">
            <label for="auth_config_json">鉴权配置 (JSON)</label>
            <textarea
              id="auth_config_json"
              class="auth-config-area"
              .value=${this.form.auth_config_json}
              ?disabled=${this.loading}
              placeholder='{"token":"xxx","header_name":"X-API-Key"}'
              spellcheck="false"
              @input=${(e: Event) =>
                    this.updateFormField('auth_config_json', (e.target as HTMLTextAreaElement).value)}
            ></textarea>
            <div class="field-hint">JSON 格式的鉴权参数，如 token、header 名称等</div>
          </div>

          <div class="field span-2">
            <label for="remark">备注</label>
            <textarea
              id="remark"
              .value=${this.form.remark}
              ?disabled=${this.loading}
              placeholder="可选备注信息"
              @input=${(e: Event) =>
                    this.updateFormField('remark', (e.target as HTMLTextAreaElement).value)}
            ></textarea>
          </div>
        </div>

        <div class="actions">
          <button
            type="button"
            class="btn-primary"
            ?disabled=${this.saving || this.loading}
            @click=${() => this.saveSystem()}
          >
            ${this.saving ? '保存中…' : this.selectedCode ? '更新配置' : '创建系统'}
          </button>
          ${this.selectedCode
                ? html`
              <button
                type="button"
                class="btn-outline"
                ?disabled=${this.saving || this.loading}
                @click=${() => this.createNew()}
              >
                取消编辑
              </button>
              <button
                type="button"
                class="btn-danger"
                ?disabled=${this.saving || this.loading}
                @click=${() => this.deleteSystem()}
              >
                删除系统
              </button>
            `
                : null}
        </div>

        ${this.persistHint
                ? html`<div class="persist-hint ${hintClass}">${this.persistHint}</div>`
                : null}
      </section>
    `;
    }

    render() {
        return html`
      <div class="page-wrap">
        ${this.renderSidebar()}
        ${this.renderForm()}
      </div>
    `;
    }
}
